import { Pencil } from 'lucide-react';
import { cn } from './ui';

/* =============================================================================
 * The office's own icons
 * -----------------------------------------------------------------------------
 * Three drawings the office supplied: a green pin with a ring in it, the same
 * pin struck through in red, and a pencil. They mean one thing each, everywhere
 * in the product and on every document it prints.
 *
 * -----------------------------------------------------------------------------
 * WHY THEY LIVE HERE AND NOT IN THE SCREENS
 * -----------------------------------------------------------------------------
 * They were already drawn three times, and the three had already drifted.
 *
 *   payslipPdf.scanPin draws the pin in jsPDF primitives at #1B8A3C / #E01A2B;
 *   PayslipSheet.ScanPin draws the same pin in SVG at #3F8A55 / #B84444;
 *   everywhere else used lucide's MapPin and MapPinOff, which are a stroked
 *   outline with no ring at all, taking whatever colour the line above them
 *   happened to set.
 *
 * So one fact — this scan has a position, this one does not — was three
 * pictures in three different greens. This is the single definition, in the
 * artwork's own colours. dayTones.ts exists for the same reason: two tables of
 * colours is one of them going stale.
 *
 * The printed pin stays in payslipPdf, because jsPDF cannot take an SVG, but
 * it reads its colours from here so the two can no longer disagree.
 * ========================================================================== */

/**
 * The pin's colours, flattened out of the artwork.
 *
 * The drawings are gradients — #1C8C44 down to #146C34, and #E4040C down to
 * #AC0404. A gradient is a lie at eleven pixels: it costs a defs block, it
 * prints as a band, and at the sizes this icon is actually used it reads as
 * one colour regardless. These are that one colour.
 */
export const PIN_INK = { on: '#17803C', off: '#D00408' } as const;

/** And the pencil's, which the artwork draws flat. */
export const EDIT_INK = '#7C7C7C';

export interface PinProps {
  /** Struck through: no position, or a position outside the boundary. */
  off?: boolean;
  /** The width. The teardrop is drawn slightly taller than it is wide. */
  size?: number;
  /**
   * Take the surrounding text colour instead of the artwork's.
   *
   * For the places where the pin is furniture rather than a verdict — the grey
   * placeholder inside an empty map, a label beside a figure. A red pin there
   * would be reporting a fault that has not happened.
   */
  inherit?: boolean;
  className?: string;
}

/**
 * A position, or the lack of one.
 *
 * The ring is the whole icon. A coloured teardrop with nothing in it is a blob
 * at small sizes and could be anything; the white ring with a dot at its
 * centre is what makes it read as a map pin at a glance, and it is the part of
 * the drawing that survives being photocopied in grey.
 */
export function LocationPin({
  off = false, size = 14, inherit = false, className,
}: PinProps) {
  const tone = inherit ? 'currentColor' : (off ? PIN_INK.off : PIN_INK.on);

  return (
    <svg
      width={size}
      height={size * 1.15}
      viewBox="0 0 120 140"
      aria-hidden
      className={cn('inline-block shrink-0', className)}
    >
      <path
        d="M60 8 C 34 8 14 28 14 54 C 14 76 30 100 60 130 C 90 100 106 76 106 54 C 106 28 86 8 60 8 Z"
        fill={tone}
      />
      {/* The ring, and the pin's own colour at its centre. */}
      <circle cx="60" cy="52" r="17" fill="#FFFFFF" />
      <circle cx="60" cy="52" r="9" fill={tone} />

      {off && (
        <>
          {/* The slash is laid on a white one first, so it reads as a cut
              THROUGH the pin rather than a line resting on top of it. That
              white edge is in the artwork, and it is what keeps the stroke
              legible where it crosses the ring.

              The two widths are the whole difficulty. At 26 against 14 — which
              is roughly the proportion the artwork shows at 400 pixels — the
              white eats the pin and eleven pixels of it is a red smear with no
              pin left in it. 19 against 12, pulled in off the corners, keeps a
              pin that is visibly struck through down to seven. Checked at
              every size the app uses before these numbers were chosen. */}
          <line
            x1="22" y1="94" x2="98" y2="12"
            stroke="#FFFFFF" strokeWidth="19" strokeLinecap="round"
          />
          <line
            x1="22" y1="94" x2="98" y2="12"
            stroke={tone} strokeWidth="12" strokeLinecap="round"
          />
        </>
      )}
    </svg>
  );
}

/**
 * The pin as a plain glyph, taking whatever colour surrounds it.
 *
 * Same drawing, same meaning, no verdict. It exists because three places pass
 * an icon as a value rather than render one — a tab's definition, a list of
 * features on the sign-in page, a section heading — and a value cannot carry
 * `inherit`. Its signature is a lucide icon's, so it drops into those without
 * the consumers knowing anything changed.
 */
export function LocationGlyph({
  size, className,
}: {
  size?: number;
  className?: string;
  /**
   * Taken and ignored, both of them.
   *
   * Callers that hold an icon as a value hand every icon the same props —
   * `strokeWidth` because lucide's are outlines, `aria-hidden` because they
   * are decoration. This pin is a filled shape and already hides itself, so
   * neither means anything here; declaring them is what lets it stand in a
   * list beside twelve lucide icons without the list having to know.
   */
  strokeWidth?: number;
  'aria-hidden'?: boolean | 'true' | 'false';
}) {
  return <LocationPin size={size} className={className} inherit />;
}

/**
 * Edit. One pencil wherever something can be changed.
 *
 * A wrapper rather than a traced path, and deliberately: the artwork is
 * lucide's Pencil — same angle, same outline, same ferrule line — so tracing
 * it again would be a second copy of a drawing already here, free to drift by
 * a degree and a half.
 *
 * What was actually inconsistent was never that shape. It was that four places
 * in the attendance screen drew `PencilLine` instead — a different glyph, for
 * the same act — and that the geofence editor's edit button had no icon at
 * all. One import fixes all of it, and makes the next one hard to get wrong.
 *
 * -----------------------------------------------------------------------------
 * THE SIZE IS A PROP, RELUCTANTLY
 * -----------------------------------------------------------------------------
 * It was not, for about ten minutes. A caller that can choose is a caller that
 * will choose differently from the one beside it, and that is how 11, 13, 14
 * and 15 all ended up in the same product.
 *
 * But the payslip's day strip marks a corrected day with a pencil inside a
 * cell fifteen points wide, and the key under it at eleven. Those are not
 * somebody failing to agree with the button on the next screen; they are a
 * different thing at a different scale. A fixed 14 would have burst the strip.
 *
 * So: a default that covers nearly every caller, and a number only where the
 * space genuinely dictates one.
 */
export function EditPencil({
  size = 14, className,
}: { size?: number; className?: string }) {
  return <Pencil size={size} className={cn('shrink-0', className)} />;
}
